/* eslint-disable consistent-return */
import { Box, Hidden, Stack, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import React, { useEffect, useRef } from 'react';

import i18n from '@utils/i18n';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import useResponsive from '@hooks/useResponsive';
import env from '@constants/env';

const bannerImg = `${env.s3BaseUrl}/landing-page/homepage/media-banner.jpg`;
const bannerImgMobile = `${env.s3BaseUrl}/landing-page/homepage/media-banner-mobile.png`;
const t = i18n.getFixedT(null, null, 'pages.mission');
gsap.registerPlugin(ScrollTrigger);
const Banner = () => {
  const componentRef = useRef(null);
  const imageRef = useRef(null);
  const isSm = useResponsive('down', 1000);
  const isTablet = useResponsive('between', 0, 500, 1000);
  useEffect(() => {
    // Register the ScrollTrigger plugin

    // Ensure refs are available
    if (componentRef.current && !isSm) {
      // Create timeline with scroll trigger
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: componentRef.current,
          start: 'top top',
          end: '+=150%',

          scrub: 1, // This is essential for scroll-based animation
          markers: false,
          id: 'hotplace-slider'
        }
      });

      // Add animation to timeline

      timeline.to(componentRef.current, {
        opacity: 0
      });
      timeline.to(imageRef.current, {
        opacity: 0
      });

      return () => {
        if (timeline.scrollTrigger) {
          timeline.scrollTrigger.kill();
        }
      };
    }
  }, []);
  return (
    <Stack
      sx={{
        width: '100%',
        minHeight: { md: '100svh', xs: '100svh' },
        mt: { md: '-54px', xs: '-54px' },
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}
      ref={componentRef}
    >
      <Stack
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: {
            md: 'column'
          }
        }}
      >
        <Stack
          sx={{
            backgroundColor: '#202020',
            opacity: 0,
            zIndex: 10,
            position: 'absolute',

            top: 0,
            left: 0,
            right: 0,

            width: '100%',
            height: '100%',
            bottom: 0
          }}
        />
        <Stack
          sx={{
            width: { md: '75%', lg: '100%' },
            px: { lg: '320px' },
            maxWidth: '1920px',
            minHeight: '200px',
            height: '30vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 5
          }}
          data-aos="fade-up"
          data-aos-offset="0"
          data-aos-duration="600"
        >
          <Typography
            fontWeight={700}
            fontFamily="Pretendard"
            sx={{
              color: '#FFF',
              fontSize: { md: 48, xs: 24 },

              letterSpacing: { xs: '-0.72px', md: '-2.4px' }
            }}
          >
            {t('news-title')}
          </Typography>
        </Stack>

        <Stack
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: 0
          }}
        >
          {bannerImg ? (
            <Box
              component="img"
              ref={imageRef}
              src={bannerImg}
              sx={{
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: -1,
                objectFit: 'cover',
                width: '100%'
              }}
            />
          ) : (
            <Skeleton
              sx={{ bgcolor: 'grey.400' }}
              variant="rectangular"
              width="100%"
              height="100%"
            />
          )}
        </Stack>
      </Stack>

      <Hidden mdUp>
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            zIndex: 100
          }}
        >
          <Stack
            sx={{
              height: 'auto',
              px: 2.5,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: {
                md: 'column'
              },
              py: 2.5,
              pt: 3.5
            }}
            data-aos="fade-up"
            data-aos-offset="50"
            data-aos-duration="600"
          >
            <Typography
              fontWeight={700}
              fontFamily="Pretendard"
              sx={{
                color: '#FFF',
                fontSize: { md: 48, xs: 24 },
                mb: { md: '40px' },
                letterSpacing: { xs: '-0.72px', md: '-2.4px' }
              }}
            >
              {t('news-title')}
            </Typography>
          </Stack>
        </Stack>
        <img
          src={isTablet ? bannerImg : bannerImgMobile}
          alt="mobile banner"
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 0,
            position: 'absolute',
            objectFit: 'cover',
            top: 0,
            left: 0,
            zIndex: 0
          }}
        />
      </Hidden>
    </Stack>
  );
};

export default Banner;
